import React, { useRef, useEffect } from 'react';

const RichTextEditor = ({ value, onChange, placeholder = 'Write your content here...' }) => {
    const editorRef = useRef(null);

    useEffect(() => {
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value || '';
        }
    }, [value]);

    const handleInput = () => {
        if (onChange && editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const execCommand = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
        handleInput();
    };

    const handlePaste = (e) => {
        // Allow images to be pasted
        const items = e.clipboardData?.items;
        if (items) {
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const blob = items[i].getAsFile();
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const img = `<img src="${event.target.result}" style="max-width: 100%; height: auto; margin: 20px 0; border-radius: 10px;" />`;
                        document.execCommand('insertHTML', false, img);
                        handleInput();
                    };
                    reader.readAsDataURL(blob);
                    e.preventDefault();
                    return;
                }
            }
        }
    };

    const toolbarButtonStyle = {
        padding: '10px 14px',
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(201, 169, 110, 0.2)',
        borderRadius: '8px',
        color: 'var(--text-light)',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    };

    return (
        <div style={{ width: '100%' }}>
            {/* Toolbar */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(201, 169, 110, 0.2)',
                borderBottom: 'none',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px'
            }}>
                <button
                    type="button"
                    onClick={() => execCommand('bold')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Bold"
                >
                    <strong>B</strong>
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('italic')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Italic"
                >
                    <em>I</em>
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('underline')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Underline"
                >
                    <u>U</u>
                </button>
                <div style={{ width: '1px', background: 'rgba(201, 169, 110, 0.2)', margin: '0 4px' }}></div>
                <button
                    type="button"
                    onClick={() => execCommand('formatBlock', 'h2')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Heading 2"
                >
                    H2
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('formatBlock', 'h3')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Heading 3"
                >
                    H3
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('formatBlock', 'p')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Paragraph"
                >
                    P
                </button>
                <div style={{ width: '1px', background: 'rgba(201, 169, 110, 0.2)', margin: '0 4px' }}></div>
                <button
                    type="button"
                    onClick={() => execCommand('insertUnorderedList')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Bullet List"
                >
                    • List
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('insertOrderedList')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Numbered List"
                >
                    1. List
                </button>
                <div style={{ width: '1px', background: 'rgba(201, 169, 110, 0.2)', margin: '0 4px' }}></div>
                <button
                    type="button"
                    onClick={() => {
                        const url = prompt('Enter URL:');
                        if (url) execCommand('createLink', url);
                    }}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Insert Link"
                >
                    🔗 Link
                </button>
                <button
                    type="button"
                    onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                                if (file.size > 5 * 1024 * 1024) {
                                    alert('Image size must be less than 5MB');
                                    return;
                                }
                                try {
                                    const formData = new FormData();
                                    formData.append('image', file);
                                    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/upload/image`, {
                                        method: 'POST',
                                        body: formData
                                    });
                                    if (response.ok) {
                                        const data = await response.json();
                                        const imageUrl = `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${data.url}`;
                                        execCommand('insertImage', imageUrl);
                                    } else {
                                        alert('Upload failed');
                                    }
                                } catch (error) {
                                    alert('Error uploading image');
                                }
                            }
                        };
                        input.click();
                    }}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Insert Image"
                >
                    🖼️ Image
                </button>
                <div style={{ width: '1px', background: 'rgba(201, 169, 110, 0.2)', margin: '0 4px' }}></div>
                <button
                    type="button"
                    onClick={() => execCommand('justifyLeft')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Align Left"
                >
                    ⬅
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('justifyCenter')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Align Center"
                >
                    ↔
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('justifyRight')}
                    style={toolbarButtonStyle}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'var(--accent-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    }}
                    title="Align Right"
                >
                    ➡
                </button>
            </div>

            {/* Editor Area */}
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onPaste={handlePaste}
                data-placeholder={placeholder}
                style={{
                    minHeight: '400px',
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(201, 169, 110, 0.2)',
                    borderBottomLeftRadius: '10px',
                    borderBottomRightRadius: '10px',
                    color: 'var(--text-light)',
                    fontSize: '16px',
                    lineHeight: '1.8',
                    outline: 'none',
                    overflowY: 'auto',
                    maxHeight: '600px'
                }}
                onFocus={(e) => {
                    e.target.style.borderColor = 'var(--accent-color)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
            />
            
            <style>{`
                [contentEditable][data-placeholder]:empty:before {
                    content: attr(data-placeholder);
                    color: rgba(245, 241, 237, 0.3);
                    pointer-events: none;
                }
                [contentEditable] h2 {
                    font-size: 28px;
                    font-weight: 600;
                    margin: 20px 0 15px 0;
                    color: var(--text-light);
                }
                [contentEditable] h3 {
                    font-size: 22px;
                    font-weight: 600;
                    margin: 18px 0 12px 0;
                    color: var(--text-light);
                }
                [contentEditable] p {
                    margin: 12px 0;
                    line-height: 1.8;
                }
                [contentEditable] a {
                    color: var(--accent-color);
                    text-decoration: underline;
                }
                [contentEditable] img {
                    max-width: 100%;
                    height: auto;
                    margin: 20px 0;
                    border-radius: 10px;
                }
                [contentEditable] ul, [contentEditable] ol {
                    margin: 15px 0;
                    padding-left: 30px;
                }
                [contentEditable] li {
                    margin: 8px 0;
                }
            `}</style>
        </div>
    );
};

export default RichTextEditor;
